<template>
  <section class="delegate-question-list" aria-label="Delegate question list">
    <div class="datatable-toolbar question-toolbar">
      <div class="question-filters">
        <div class="search-control">
          <label class="sr-only" for="delegate-question-search">Search delegate questions</label>
          <input id="delegate-question-search" type="search" class="form-control" placeholder="Search questions" v-model="dataFilter.searchText" @keyup.enter="doFind">
          <button class="btn btn-outline-primary" type="button" @click="doFind">Search</button>
        </div>

        <div class="filter-control">
          <label class="sr-only" for="delegate-question-status">Filter by status</label>
          <select id="delegate-question-status" v-model="dataFilter.status" class="form-control">
            <option value="">All statuses</option>
            <option v-for="status in statusOptions" :key="status.value" :value="status.value">{{ status.label }}</option>
          </select>
        </div>

        <div class="filter-control">
          <label class="sr-only" for="delegate-question-event">Filter by event</label>
          <select id="delegate-question-event" v-model="dataFilter.event" class="form-control">
            <option value="">All events</option>
            <option v-for="event in eventOptions" :key="event" :value="event">{{ event }}</option>
          </select>
        </div>

        <button v-if="hasActiveFilters" class="btn btn-link text-secondary clear-filters" type="button" @click="clearFilters">Clear filters</button>
      </div>

        <ConfirmDialog></ConfirmDialog>
    </div>

    <div class="table-responsive table-container question-table-wrapper">
      <table class="table table-striped question-table">

        <thead>
        <tr>
          <th scope="col">No</th>
          <th scope="col">Question</th>
          <th scope="col">Event Name</th>
          <th scope="col">Question By</th>
          <th scope="col">Question To</th>
          <th scope="col">Status</th>
          <th scope="col">Actions</th>
        </tr>
        </thead>

        <tbody>
        <tr v-if="isLoading">
          <td colspan="7" class="table-message">
            <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            Loading delegate questions…
          </td>
        </tr>
        <tr v-else-if="filteredDataList.length === 0">
          <td colspan="7" class="table-message empty-state">
            <i class="pi pi-comments" aria-hidden="true"></i>
            <strong>{{ hasActiveFilters ? 'No delegate questions match your filters.' : 'No delegate questions yet.' }}</strong>
            <span>{{ hasActiveFilters ? 'Try a different keyword or clear the active filters.' : 'Questions submitted by delegates will appear here.' }}</span>
            <button v-if="hasActiveFilters" class="btn btn-outline-primary btn-sm" type="button" @click="clearFilters">Clear filters</button>
          </td>
        </tr>
        <template v-else>
        <tr v-for="(data, index) in filteredDataList" :key="data.id">
          <td class="order-number">{{ index + 1 }}</td>
          <td class="text-left question-preview" :title="data.questionText">{{ data.questionText }}</td>
          <td class="text-left event-name">{{ data.eventDetailName }}</td>
          <td class="text-left">{{ data.memberName }}</td>
          <td class="text-left">{{ data.speakerName }}</td>
          <td><span :class="['badge question-status', getStatusClass(data)]">{{ data.statusLabel }}</span></td>
          <td class="actions">
            <button type="button" class="btn btn-outline-secondary" aria-label="View full question" v-tooltip="'View full question'" @click="onViewQuestion(data)"><i class="pi pi-eye" aria-hidden="true"></i></button>
            <button type="button" class="btn btn-outline-success" aria-label="Resolve question" v-tooltip="'Resolve question'" @click="onResolve(data)" v-if="Number(data.status) === 1"><i class="pi pi-check-circle" aria-hidden="true"></i></button>
            <button type="button" class="btn btn-outline-danger" aria-label="Remove question" v-tooltip="'Remove question'" @click="onRemove(data)"><i class="pi pi-trash" aria-hidden="true"></i></button>
          </td>
        </tr>
        </template>

        </tbody>

      </table>

    </div>

    <Dialog v-model:visible="showQuestionDialog" modal header="Delegate question" :style="{ width: '34rem' }" :breakpoints="{ '767px': '92vw' }">
      <div class="question-dialog" v-if="selectedQuestion">
        <p class="question-dialog-text">{{ selectedQuestion.questionText }}</p>
        <dl>
          <div><dt>Event</dt><dd>{{ selectedQuestion.eventDetailName || '—' }}</dd></div>
          <div><dt>Asked by</dt><dd>{{ selectedQuestion.memberName || '—' }}</dd></div>
          <div><dt>Directed to</dt><dd>{{ selectedQuestion.speakerName || '—' }}</dd></div>
        </dl>
      </div>
    </Dialog>

  </section>
</template>

<script>
import {defineComponent} from 'vue'
import ConfirmDialog from "primevue/confirmdialog";
import Dialog from "primevue/dialog";
import apiService from "@/services/ApiService";

export default defineComponent({
    name: "EventDetailQuestionList",
    components: {
        ConfirmDialog,
        Dialog
    },
    data() {
        return {
            dataList: [],
            isLoading: true,
            showQuestionDialog: false,
            selectedQuestion: null,
            dataFilter: {
                searchText: '',
                status: '',
                event: ''
            }
        }
    },
    computed: {
        filteredDataList() {
            return this.dataList.filter((question) => {
                const matchesStatus = !this.dataFilter.status || String(question.status) === this.dataFilter.status;
                const matchesEvent = !this.dataFilter.event || question.eventDetailName === this.dataFilter.event;
                return matchesStatus && matchesEvent;
            });
        },
        statusOptions() {
            const statuses = new Map();
            this.dataList.forEach((question) => {
                if (question.statusLabel) {
                    statuses.set(String(question.status), question.statusLabel);
                }
            });
            return Array.from(statuses, ([value, label]) => ({value, label}));
        },
        eventOptions() {
            return [...new Set(this.dataList.map((question) => question.eventDetailName).filter(Boolean))];
        },
        hasActiveFilters() {
            return Boolean(this.dataFilter.searchText || this.dataFilter.status || this.dataFilter.event);
        }
    },
    methods: {
        onViewQuestion(data) {
            this.selectedQuestion = data;
            this.showQuestionDialog = true;
        },
        onRemove(data) {
            this.$confirm.require({
                message: 'Are you sure you want to remove ?',
                header: 'Confirmation',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.doRemove(data.id);
                }
            });
        },
        onResolve(data) {
            this.$confirm.require({
                message: 'Are you sure you want to resolve ?',
                header: 'Confirmation',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.doResolve(data.id);
                }
            });
        },
        doFind() {
            const params = {
                SearchText: this.dataFilter.searchText
            };
            this.isLoading = true;
            apiService.get('/EventDetail/question/find', {params: params})
                .then(response => {
                    this.dataList = response.data || [];
                }).catch(error => {
                console.log(error);

                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get forum question',
                        detail: error.response.data.Message,
                        life: 5000
                    });
                }).finally(() => {
                    this.isLoading = false;
                });
        },
        clearFilters() {
            this.dataFilter.searchText = '';
            this.dataFilter.status = '';
            this.dataFilter.event = '';
            this.doFind();
        },
        doRemove(id) {
            apiService.delete(`/EventDetail/question/${id}`)
                .then(() => {
                    this.doFind();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Question has been removed',
                        life: 5000
                    });

                }).catch(error => {

                console.log(error);

                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to remove question',
                    detail: error.response.data.Message,
                    life: 5000
                });
            });
        },
        doResolve(id) {
            apiService.post(`/EventDetail/question/${id}/resolve`)
                .then(() => {
                    this.doFind();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Question has been resolved',
                        life: 5000
                    });

                }).catch(error => {

                console.log(error);

                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to resolve question',
                    detail: error.response.data.Message,
                    life: 5000
                });
            });
        },
        getImageUrl(image){
            return `${apiService.defaults.baseURL}/file/${image}`;
        },
        getStatusClass(data) {
            return Number(data.status) === 1 ? 'badge-warning' : 'badge-success';
        }
    },
    mounted() {
        this.doFind();
    }
})
</script>


<style scoped>
.question-toolbar {
    margin-bottom: 1rem;
}

.question-filters {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.search-control {
    align-items: center;
    display: flex;
    gap: 0.5rem;
}

.search-control .form-control {
    width: 19rem;
}

.filter-control .form-control {
    min-width: 9.5rem;
}

.clear-filters {
    font-size: 0.875rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
}

.question-table-wrapper {
    margin: 0;
}

.question-table th {
    white-space: nowrap;
}

.question-preview {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    display: -webkit-box;
    max-width: 22rem;
    min-width: 17rem;
    overflow: hidden;
}

.event-name {
    min-width: 10rem;
}

.question-status {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.35em 0.6em;
}

.table-message {
    color: #6c757d;
    padding: 2.5rem 1rem !important;
    text-align: center;
}

.empty-state > * {
    display: block;
    margin: 0 auto;
}

.empty-state > .pi {
    color: #426cc6;
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
}

.empty-state > strong {
    color: #343a40;
    margin-bottom: 0.35rem;
}

.empty-state > span {
    margin-bottom: 1rem;
}

.question-dialog-text {
    color: #343a40;
    line-height: 1.6;
    white-space: pre-wrap;
}

.question-dialog dl {
    border-top: 1px solid #e9ecef;
    margin: 1.25rem 0 0;
    padding-top: 1rem;
}

.question-dialog dl > div {
    display: grid;
    gap: 0.5rem;
    grid-template-columns: 7rem 1fr;
    margin-bottom: 0.7rem;
}

.question-dialog dt {
    color: #6c757d;
    font-weight: 600;
}

.question-dialog dd {
    color: #343a40;
    margin: 0;
}

@media (max-width: 767.98px) {
    .question-filters,
    .search-control {
        align-items: stretch;
        flex-direction: column;
        width: 100%;
    }

    .search-control .form-control,
    .filter-control .form-control {
        width: 100%;
    }

    .clear-filters {
        align-self: flex-start;
    }
}

</style>
